import React, { useEffect, useCallback, useState, useRef } from "react";
import { Container, Row, Col, Card, CardBody, CardHeader, Input, Label, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { socket } from "../../../common/socket";

// Redux actions
import { fetchSettingsData, updateSettingsAction, declareManualWinner } from "../../../slices/sorat/settings/thunk";
import { updateGameState, updateBidPredictions, setManualWinnerWindow, resetRound, setSelectedWinner } from "../../../slices/sorat/settings/reducer";

import { toast } from "react-toastify";

import "./Dashboard.css";
import { dashboardCache } from "../../../helpers/dashboardCache";
import { updateHistory } from "../../../slices/sorat/settings/reducer";
import redDiamondImg from "../../../assets/images/red.png";
import blackDiamondImg from "../../../assets/images/black.png";

import {
    PHASE_COLORS,
    MULTIPLIER_OPTIONS,
    SITE_TITLE,
    GAME_MODES,
    RETRY_INTERVAL
} from "../../../common/constants";
import { getSoratChannel, soratSocketEvents } from "../../../common/soratChannels";

const SORAT_ITEMS = [
    { id: 1, name: "Umbrella", icon: "ri-umbrella-line" },
    { id: 2, name: "Football", icon: "ri-football-line" },
    { id: 3, name: "Sun", icon: "ri-sun-line" },
    { id: 4, name: "Diya", icon: "ri-fire-line" },
    { id: 5, name: "Elephant", icon: "ri-aliens-line" }, 
    { id: 6, name: "Bucket", icon: "ri-cup-line" },
    { id: 7, name: "Kite", icon: "ri-send-plane-line" },
    { id: 8, name: "Top", icon: "ri-disc-line" },
    { id: 9, name: "Rose", icon: "ri-plant-line" },
    { id: 10, name: "Butterfly", icon: "ri-bug-line" },
    { id: 11, name: "Pigeon", icon: "ri-twitter-x-line" },
    { id: 12, name: "Rabbit", icon: "ri-bug-2-line" }
];

const Dashboard = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [platform, setPlatform] = useState("web");
    const channel = getSoratChannel(platform);
    const [customMultiplier, setCustomMultiplier] = useState("");
    const [showCustomModal, setShowCustomModal] = useState(false);
    const [lastTenWinners, setLastTenWinners] = useState(() => dashboardCache.get("sorat-web") || []);
    const [currentRoundWinner, setCurrentRoundWinner] = useState(null);
    const [isSocketConnected, setIsSocketConnected] = useState(socket.connected);
    const [hoveredWinner, setHoveredWinner] = useState(null);
    const [onlineCount, setOnlineCount] = useState(0);
    const [finalWinners, setFinalWinners] = useState({});
    const finalWinnerRef = useRef({});

    const {
        settings,
        timerDisplay,
        phase,
        roundNumber,
        bidDetails,
        totalAmount,
        isManualWinnerWindowOpen,
        selectedWinner,
        loading,
        globalReturnMultiplier,
        lastTenWinners: reduxHistory // Select from Redux
    } = useSelector((state) => state.so.settings || {});

    // Sync local state with Redux on load
    useEffect(() => {
        if (reduxHistory && reduxHistory.length > 0) {
            setLastTenWinners(reduxHistory);
        }
    }, [reduxHistory]);

    // Fetch settings on mount
    useEffect(() => {
        dispatch(fetchSettingsData(platform));
    }, [dispatch, platform]);

    // Unified Socket Lifecycle Hook
    useEffect(() => {
        const events = soratSocketEvents(platform);
        const cacheKey = `sorat-${platform}`;
        const isThisChannel = (data) => !data?.platform || data.platform === platform;

        dispatch(resetRound());
        setCurrentRoundWinner(null);
        setLastTenWinners(dashboardCache.get(cacheKey) || []);

        const requestHistory = () => {
            socket.emit(events.HISTORY);
            socket.emit(events.GET_USER_LAST_BID);
            socket.emit("getGameOnlineStats", { gameType: channel.gameType });
        };

        const handleTimerUpdate = (data) => {
            if (!isThisChannel(data)) return;
            dispatch(updateGameState({
                timeLeft: data.timeLeft || data.timer || 0,
                phase: data.phase,
                roundNumber: data.roundNumber,
                status: data.status
            }));
        };

        const handleUserLastBidResponse = (data) => {
            const bidData = data.data || data;
            if (!bidData) return;

            const numberTotals = {};
            const bids = {};
            let totalAmountToDispatch = 0;

            const processSingleBet = (bet) => {
                const { betValues, betValue, amount, number, point } = bet;
                let actualValues = [];
                if (betValues && Array.isArray(betValues)) actualValues = betValues;
                else if (betValue) actualValues = Array.isArray(betValue) ? betValue : [betValue];
                else if (number !== undefined) actualValues = [number];

                const actualAmount = amount !== undefined ? amount : (point !== undefined ? point : 0);

                if (actualValues.length === 0) return;

                const key = String(actualValues[0]);
                numberTotals[key] = (numberTotals[key] || 0) + actualAmount;
                bids[key] = (bids[key] || 0) + 1;
            };

            if (Array.isArray(bidData.bets)) {
                totalAmountToDispatch = bidData.totalBetAmount || 0;
                bidData.bets.forEach(processSingleBet);
            } else if (bidData.number !== undefined || bidData.betValues || bidData.betType) {
                totalAmountToDispatch = bidData.totalAmount || bidData.amount || bidData.point || 0;
                processSingleBet(bidData);
            } else {
                return;
            }

            dispatch(updateBidPredictions({
                bids,
                totalAmount: totalAmountToDispatch,
                numberTotals,
                isIncremental: true
            }));
        };

        const handleLastTenWinners = (data) => {
            let winnersArray = [];
            if (Array.isArray(data)) winnersArray = data;
            else if (data && data.lastTenWinners) winnersArray = data.lastTenWinners;
            else if (data && data.data) {
                if (Array.isArray(data.data)) winnersArray = data.data;
                else if (data.data.lastTenWinners) winnersArray = data.data.lastTenWinners;
            }

            if (winnersArray && winnersArray.length > 0) {
                dashboardCache.set(cacheKey, winnersArray);
                setLastTenWinners(winnersArray);
                dispatch(updateHistory(winnersArray)); // Sync Redux
            }
        };

        const handleBidUpdate = (data) => {
            if (!isThisChannel(data)) return;
            let bids = data.bids || data.predictions || {};
            let numberTotals = data.numberTotals || data.bidAmounts || {};
            
            if (data.bets && Array.isArray(data.bets)) {
                bids = {};
                numberTotals = {};
                data.bets.forEach(bet => {
                    const sym = bet.symbol !== undefined ? bet.symbol : (bet.bidNumber !== undefined ? bet.bidNumber : bet.number);
                    if (sym !== undefined) {
                        const key = String(sym);
                        bids[key] = (bids[key] || 0) + 1;
                        numberTotals[key] = (numberTotals[key] || 0) + (bet.betAmount || bet.amount || 0);
                    }
                });
            }

            dispatch(updateBidPredictions({
                bids: bids,
                totalAmount: data.totalAmount || 0,
                numberTotals: numberTotals,
                isIncremental: true
            }));
        };

        const handleManualWinnerWindow = (data) => {
            if (!isThisChannel(data)) return;
            const isOpen = data.windowOpen !== undefined ? data.windowOpen : (data.isOpen !== undefined ? data.isOpen : true);
            dispatch(setManualWinnerWindow(isOpen));
        };

        const handleNewRound = (data) => {
            if (!isThisChannel(data)) return;
            dispatch(resetRound());
            setCurrentRoundWinner(null);
            if (data && data.lastTenWinners && Array.isArray(data.lastTenWinners)) {
                dashboardCache.set(cacheKey, data.lastTenWinners);
                setLastTenWinners(data.lastTenWinners);
                dispatch(updateHistory(data.lastTenWinners)); // Sync Redux
            } else {
                requestHistory();
            }
        };

        const handlePlaySpin = (data) => {
            if (!isThisChannel(data)) return;
            if (data && (data.winningNumber !== undefined || data.winningSymbol !== undefined)) {
                setCurrentRoundWinner({
                    roundNumber: data.roundNumber,
                    winningNumber: data.winningSymbol !== undefined ? data.winningSymbol : data.winningNumber,
                    winnerInfo: data.winnerInfo,
                    timestamp: data.timestamp
                });
            }
            dispatch(setManualWinnerWindow(false)); // Lock on spin
        };

        const handleRoundCompleted = (data) => {
            if (!isThisChannel(data)) return;
            if (data && (data.winningSymbol !== undefined || data.winningNumber !== undefined)) {
                setCurrentRoundWinner({
                    roundNumber: data.roundNumber,
                    winningNumber: data.winningSymbol !== undefined ? data.winningSymbol : data.winningNumber,
                    winnerInfo: data.winnerInfo,
                    timestamp: data.timestamp
                });
            }
            dispatch(setManualWinnerWindow(false));
        };

        const handlePlaceBid = (data) => {
            if (data && data.success && data.data) {
                const { numberTotals, bids, totalAmount: newTotalAmount } = data.data;
                const bidCounts = {};
                if (bids && Array.isArray(bids)) {
                    bids.forEach(bid => {
                        const num = bid.bidNumber;
                        bidCounts[num] = (bidCounts[num] || 0) + 1;
                    });
                }
                dispatch(updateBidPredictions({
                    bids: bidCounts,
                    totalAmount: newTotalAmount,
                    numberTotals: numberTotals || {},
                    isIncremental: true
                }));
            }
        };

        const handleConnect = () => {
            setIsSocketConnected(true);
            requestHistory();
        };

        const handleDisconnect = () => {
            setIsSocketConnected(false);
        };

        const handleOnlineStats = (data) => {
            if (data && data.gameType === channel.gameType) {
                setOnlineCount(data.onlineUsersCount);
            }
        };

        // 1. Register Listeners FIRST
        socket.on(events.TIMER, handleTimerUpdate);
        socket.on(events.HISTORY, handleLastTenWinners);
        socket.on(events.USER_LAST_BID_RESPONSE, handleUserLastBidResponse);
        socket.on(events.BID_UPDATE, handleBidUpdate);
        socket.on(events.MANUAL_WINDOW, handleManualWinnerWindow);
        socket.on(events.NEW_ROUND, handleNewRound);
        socket.on(events.ROUND_COMPLETED, handleRoundCompleted);
        socket.on(events.PLACE_BID, handlePlaceBid);
        socket.on(events.PLAY_SPIN, handlePlaySpin);
        socket.on(events.HISTORY_FALLBACK, handleLastTenWinners);
        socket.on("connect", handleConnect);
        socket.on("disconnect", handleDisconnect);
        socket.on("admin-onlineStatsResponse", handleOnlineStats);

        // 2. Initial State & Request
        setIsSocketConnected(socket.connected);
        requestHistory();

        // 3. Retry mechanism if still empty
        const retryTimer = setInterval(() => {
            if (lastTenWinners.length === 0 && socket.connected) {
                requestHistory();
            }
        }, RETRY_INTERVAL);

        return () => {
            clearInterval(retryTimer);
            socket.off(events.TIMER, handleTimerUpdate);
            socket.off(events.HISTORY, handleLastTenWinners);
            socket.off(events.USER_LAST_BID_RESPONSE, handleUserLastBidResponse);
            socket.off(events.BID_UPDATE, handleBidUpdate);
            socket.off(events.MANUAL_WINDOW, handleManualWinnerWindow);
            socket.off(events.NEW_ROUND, handleNewRound);
            socket.off(events.ROUND_COMPLETED, handleRoundCompleted);
            socket.off(events.PLACE_BID, handlePlaceBid);
            socket.off(events.PLAY_SPIN, handlePlaySpin);
            socket.off(events.HISTORY_FALLBACK, handleLastTenWinners);
            socket.off("connect", handleConnect);
            socket.off("disconnect", handleDisconnect);
            socket.off("admin-onlineStatsResponse", handleOnlineStats);
        };
    }, [dispatch, platform, channel.gameType, lastTenWinners.length]);

    // Handle jackpot multiplier change
    const handleMultiplierChange = useCallback(async (multiplier) => {
        const result = await dispatch(updateSettingsAction({
            platform,
            globalReturnMultiplier: multiplier,
            manualWinnerEnabled: true,
            gameMode: settings.gameMode || GAME_MODES.MEDIUM
        }));

        if (result && result.success) {
            toast.success("Settings updated successfully");
        } else if (result && result.error) {
            toast.error(result.error);
        }
    }, [dispatch, settings, platform]);

    // Handle custom multiplier submit
    const handleCustomSubmit = () => {
        const value = parseInt(customMultiplier, 10);
        if (value && value > 0) {
            handleMultiplierChange(value);
            setShowCustomModal(false);
            setCustomMultiplier("");
        }
    };

    // First successful choice for this round and channel is final.
    const lockKey = `${platform}-${roundNumber || 0}`;
    const finalWinner = finalWinners[lockKey] ?? null;

    const handleManualWinner = useCallback(async (number) => {
        if (!isManualWinnerWindowOpen || finalWinnerRef.current[lockKey] != null) return;

        finalWinnerRef.current[lockKey] = number;
        const result = await dispatch(declareManualWinner(number, platform));
        if (result && result.success) {
            setFinalWinners((prev) => ({ ...prev, [lockKey]: number }));
            dispatch(setSelectedWinner(number));
            toast.success(`Winner ${number} is set and cannot be changed`);
        } else {
            delete finalWinnerRef.current[lockKey];
            if (result && result.error) toast.error(result.error);
        }
    }, [dispatch, isManualWinnerWindowOpen, platform, lockKey]);

    document.title = `Sorat ${channel.label} | ${SITE_TITLE}`;

    const multiplierOptions = MULTIPLIER_OPTIONS;
    const currentMultiplier = globalReturnMultiplier ? Number(globalReturnMultiplier) : null;
    const isCustomMultiplier = currentMultiplier && !multiplierOptions.includes(currentMultiplier);

    const getPhaseColor = () => {
        return PHASE_COLORS[phase] || PHASE_COLORS.default;
    };

    // Calculate payout and admin P&L for a selected/hovered winning number (Sorat)
    const calculateSoratPayoutAndPL = (winNum) => {
        if (winNum === null || winNum === undefined) return null;
        const winStr = String(winNum);
        let totalPayout = 0;
        let totalCollected = 0;

        Object.entries(bidDetails).forEach(([key, detail]) => {
            const amount = Number(detail.totalAmount) || 0;
            totalCollected += amount;

            let multiplier = 0;
            if (key === winStr) {
                multiplier = 10; // Standard Sorat payout is typically 10x
            }

            if (multiplier > 0) {
                totalPayout += amount * multiplier;
            }
        });

        const adminPL = totalCollected - totalPayout;
        return { payout: totalPayout, adminPL, totalCollected };
    };

    const shownWinner = finalWinner != null ? finalWinner : selectedWinner;
    const activeManualNumber = finalWinner != null ? finalWinner : (hoveredWinner !== null ? hoveredWinner : selectedWinner);
    const manualCalc = calculateSoratPayoutAndPL(activeManualNumber);

    // Sorat Constants


    const formatAmount = (val) => {
        if (typeof val !== 'number') return val;
        if (val === 0) return "0";
        return Number(val % 1 !== 0 ? val.toFixed(2) : val).toLocaleString();
    };

    const renderSoratTable = (isManual = false) => {
        return (
            <div className="sorat-container">
                <div className={`sorat-grid ${isManual ? 'manual-mode' : ''}`}>
                    {SORAT_ITEMS.map((item) => {
                        const numStr = String(item.id);
                        const details = bidDetails[numStr] || { count: 0, totalAmount: 0 };
                        const bidCount = details.count || 0;
                        const amount = details.totalAmount || 0;
                        const hasBids = amount > 0;
                        const isSelected = shownWinner?.toString() === numStr;
                        const isHovered = hoveredWinner?.toString() === numStr;

                        const content = (
                            <div className={`sorat-cell-content ${hasBids ? 'has-bids' : ''}`}>
                                {hasBids && (
                                    <div className="sorat-bet-check-icon">
                                        <i className="ri-check-line"></i>
                                    </div>
                                )}
                                <div className="sorat-item-number">{item.id}</div>
                                <div className="sorat-icon-wrapper">
                                    <i className={`${item.icon} sorat-icon`}></i>
                                </div>
                                <div className="sorat-item-name">{item.name}</div>
                                <div className="sorat-bid-point-wrapper">
                                    <span className="sorat-cell-bid">bid: {bidCount}</span>
                                    <span className="sorat-cell-point">point: {formatAmount(amount)}</span>
                                </div>
                            </div>
                        );

                        if (isManual) {
                            return (
                                <button
                                    key={item.id}
                                    className={`sorat-cell manual-winner-cell-btn ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}
                                    onClick={() => handleManualWinner(item.id)}
                                    onMouseEnter={() => {
                                        if (finalWinner == null) setHoveredWinner(item.id);
                                    }}
                                    onMouseLeave={() => setHoveredWinner(null)}
                                    disabled={!isManualWinnerWindowOpen || loading || finalWinner != null}
                                    title={
                                        finalWinner != null
                                            ? `Winner ${finalWinner} is final for this round`
                                            : !isManualWinnerWindowOpen
                                                ? "Manual winner window is closed"
                                                : `Select ${item.name} as winner`
                                    }
                                >
                                    {content}
                                </button>
                            );
                        }

                        return (
                            <div key={item.id} className={`sorat-cell ${hasBids ? 'has-bids' : ''}`}>
                                {content}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <React.Fragment>
            <div className="page-content" style={{ marginBottom: '35px' }}>
                <Container fluid>
                    {/* Header with Timer */}
                    <div className="dashboard-header">
                        <div>
                            <h4 className="dashboard-title d-flex align-items-center mb-2">
                                Sorat {channel.label}
                            </h4>
                            <div className="round-info">
                                <span className="round-number">Round #{roundNumber || 0}</span>
                                <span className="phase-badge" style={{ backgroundColor: getPhaseColor() }}>
                                    {(phase || "waiting").toUpperCase()}
                                </span>
                                {currentRoundWinner && (
                                    <span className="current-winner-badge" style={{
                                        backgroundColor: "var(--vz-success)",
                                        color: "white",
                                        padding: "4px 12px",
                                        borderRadius: "4px",
                                        marginLeft: "8px",
                                        fontSize: "14px",
                                        fontWeight: "600"
                                    }}>
                                        Winner: {currentRoundWinner.winningNumber}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="dashboard-header-actions">
                            <div className="sorat-channel-tabs" role="tablist" aria-label="Sorat channel">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={platform === "web"}
                                    className={`sorat-channel-tab ${platform === "web" ? "active" : ""}`}
                                    onClick={() => setPlatform("web")}
                                >
                                    <i className="ri-global-line"></i>
                                    Web
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={platform === "app"}
                                    className={`sorat-channel-tab ${platform === "app" ? "active" : ""}`}
                                    onClick={() => setPlatform("app")}
                                >
                                    <i className="ri-smartphone-line"></i>
                                    App
                                </button>
                            </div>
                            <div className="game-timer">{timerDisplay || "00:00"}</div>
                        </div>
                    </div>

                    {/* Settings and Last 10 Winners Row */}
                    <Row>
                        <Col md={6}>
                            {/* Settings Card */}
                            <Card className="settings-card">
                                <CardHeader>
                                    <h5 className="mb-0">Settings</h5>
                                </CardHeader>
                                <CardBody>
                                    <Row>
                                        <Col md={12}>
                                            <div className="settings-row">
                                                <Label className="settings-label">Add Jackpot:</Label>
                                                <div className="multiplier-btn-group">
                                                    {multiplierOptions.map((mult) => (
                                                        <Button
                                                            key={mult}
                                                            className={`multiplier-btn ${currentMultiplier === mult ? 'active' : ''}`}
                                                            onClick={() => handleMultiplierChange(mult)}
                                                        >
                                                            {mult}x
                                                        </Button>
                                                    ))}

                                                    {/* Show custom multiplier button if it's a custom value */}
                                                    {isCustomMultiplier && (
                                                        <Button
                                                            className="multiplier-btn active"
                                                            onClick={() => setShowCustomModal(true)}
                                                        >
                                                            {currentMultiplier}x
                                                        </Button>
                                                    )}

                                                    {/* Custom button to add new custom value */}
                                                    <Button
                                                        className="multiplier-btn custom-btn"
                                                        onClick={() => setShowCustomModal(true)}
                                                    >
                                                        + Custom
                                                    </Button>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col md={6}>
                            {/* Recent Winning Numbers Card */}
                            <Card className="settings-card">
                                <CardHeader>
                                    <h5 className="mb-0">
                                        <i className="ri-trophy-line me-2"></i>
                                        Recent Winning Numbers
                                    </h5>
                                </CardHeader>
                                <CardBody>
                                    <div className="winners-grid">
                                        {lastTenWinners.length > 0 ? (
                                            <>
                                                {lastTenWinners.map((winner, index) => {
                                                    const displayWinner = typeof winner === 'object' && winner !== null 
                                                        ? (winner.winningSymbol !== undefined ? winner.winningSymbol : (winner.winningNumber !== undefined ? winner.winningNumber : ''))
                                                        : winner;
                                                    return (
                                                        <div key={index} className="winner-number-badge">
                                                            {displayWinner}
                                                        </div>
                                                    );
                                                })}
                                            </>
                                        ) : (
                                            <div className="text-muted text-center" style={{ padding: '10px 0' }}>
                                                <i className="ri-inbox-line me-2"></i>
                                                No winning numbers recorded yet
                                            </div>
                                        )}
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Result Prediction Card */}
                    <Card className="prediction-card">
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Result Prediction</h5>
                            <span className="total-amount">
                                Total Point: {(
                                    Object.values(bidDetails).length > 0 
                                        ? Object.values(bidDetails).reduce((sum, detail) => sum + (Number(detail?.totalAmount) || 0), 0)
                                        : (Number(totalAmount) || 0)
                                ).toLocaleString()}
                            </span>
                        </CardHeader>
                        <CardBody>
                            {renderSoratTable(false)}
                        </CardBody>
                    </Card>

                    {/* Declare Result Manually Card */}
                    <Card className={`manual-winner-card ${!isManualWinnerWindowOpen ? 'disabled-card' : ''}`}>
                        <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h5 className="mb-0">
                                Declare Result Manually
                                {!isManualWinnerWindowOpen && (
                                    <span className="ms-2 badge bg-light text-dark">
                                        <i className="mdi mdi-lock me-1"></i>Locked
                                    </span>
                                )}
                                {finalWinner != null && (
                                    <span className="ms-2 badge bg-success">
                                        Final: {finalWinner}
                                    </span>
                                )}
                            </h5>
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                {manualCalc && activeManualNumber !== null && activeManualNumber !== undefined && (
                                    <div className="manual-calc-display">
                                        <span className="calc-label">Number: <strong>{activeManualNumber}</strong></span>
                                        <span className="calc-divider">|</span>
                                        <span className="calc-label">Payout: <strong className="text-warning">{manualCalc.payout.toLocaleString()}</strong></span>
                                        <span className="calc-divider">|</span>
                                        <span className="calc-label">Admin P&L: <strong className={manualCalc.adminPL >= 0 ? 'text-success' : 'text-danger'}>{manualCalc.adminPL >= 0 ? '+' : ''}{manualCalc.adminPL.toLocaleString()}</strong></span>
                                    </div>
                                )}
                                {isManualWinnerWindowOpen ? (
                                    <span className="manual-window-badge">Window Open</span>
                                ) : (
                                    <span className="manual-window-badge closed">Window Closed</span>
                                )}
                            </div>
                        </CardHeader>
                        <CardBody>
                            {renderSoratTable(true)}
                        </CardBody>
                    </Card>
                </Container>
            </div>


            {/* Custom Multiplier Modal */}
            <Modal isOpen={showCustomModal} toggle={() => setShowCustomModal(false)}>
                <ModalHeader toggle={() => setShowCustomModal(false)}>
                    Set Custom Multiplier
                </ModalHeader>
                <ModalBody>
                    <div className="custom-multiplier-input">
                        <Label>Enter Multiplier Value:</Label>
                        <Input
                            type="number"
                            value={customMultiplier}
                            onChange={(e) => setCustomMultiplier(e.target.value)}
                            placeholder="e.g., 15"
                            min="1"
                        />
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setShowCustomModal(false)}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleCustomSubmit}>
                        Apply
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
};

export default Dashboard;
